import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';

import {
  CompanyContactRecord,
  CompanyContactResponse
} from '../models/company-contact';
import {
  LinkedInProfileDetailsResponse,
  LinkedInProfileSummary,
  LinkedInProfileSection
} from '../models/linkedin-profile-details';
import { CollectDataService } from '../services/collect-data.service';
import { UiStateService } from '../services/ui-state.service';

interface LinkedInProfileTableRow {
  name: string;
  position: string;
  experience: string;
  profileUrl: string;
  contactInfo: string;
}

@Component({
  selector: 'app-contact-linkedin',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './contact-linkedin.component.html',
  styleUrl: './contact-linkedin.component.css'
})
export class ContactLinkedInComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly collectDataService = inject(CollectDataService);
  private readonly uiState = inject(UiStateService);

  protected readonly serviceId = signal<number | null>(null);
  protected readonly locationId = signal<number | null>(null);
  protected readonly contactId = signal<number | null>(null);
  protected readonly record = signal<CompanyContactRecord | null>(null);
  protected readonly loading = signal<boolean>(true);
  protected readonly errorMessage = signal<string>('');
  protected readonly linkedinLoading = signal<boolean>(false);
  protected readonly linkedinError = signal<string>('');
  protected readonly searchQuery = signal<string>('');
  protected readonly tableFilterQuery = signal<string>('');
  protected readonly linkedinDetails =
    signal<LinkedInProfileDetailsResponse | null>(null);
  protected readonly profileRows = computed<LinkedInProfileTableRow[]>(() => {
    const details = this.linkedinDetails();
    if (!details?.profiles?.length) {
      return [];
    }
    return details.profiles.map((profile) => this.buildProfileRow(profile));
  });
  protected readonly filteredProfileRows = computed<LinkedInProfileTableRow[]>(
    () => {
      const query = this.tableFilterQuery().trim().toLowerCase();
      const rows = this.profileRows();
      if (!query) {
        return rows;
      }
      return rows.filter((row) =>
        [row.name, row.position, row.experience, row.profileUrl, row.contactInfo]
          .filter((value): value is string => typeof value === 'string')
          .some((value) => value.toLowerCase().includes(query))
      );
    }
  );

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const serviceParam = params.get('serviceId');
      const locationParam = params.get('locationId');
      const contactParam = params.get('contactId');

      const parsedServiceId = serviceParam ? Number(serviceParam) : NaN;
      const parsedLocationId = locationParam ? Number(locationParam) : NaN;
      const parsedContactId = contactParam ? Number(contactParam) : NaN;

      if (
        Number.isNaN(parsedServiceId) ||
        Number.isNaN(parsedLocationId) ||
        Number.isNaN(parsedContactId)
      ) {
        this.errorMessage.set(
          'Invalid identifiers for LinkedIn profile details.'
        );
        this.loading.set(false);
        return;
      }

      this.serviceId.set(parsedServiceId);
      this.locationId.set(parsedLocationId);
      this.contactId.set(parsedContactId);
      this.uiState.setServiceId(parsedServiceId);
      this.uiState.setLocationId(parsedLocationId);
      this.loadContact(parsedServiceId, parsedLocationId, parsedContactId);
    });
  }

  protected goBackToContact(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    const contactId = this.contactId();
    if (!serviceId || !locationId || !contactId) {
      this.router.navigate(['/']);
      return;
    }
    this.router.navigate(
      ['/contacts', serviceId, locationId, contactId],
      {
        state: { linkedinReady: true, contactId }
      }
    );
  }

  protected goBackToList(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!serviceId || !locationId) {
      this.router.navigate(['/']);
      return;
    }
    this.router.navigate(['/contacts', serviceId, locationId]);
  }

  protected refreshLinkedIn(): void {
    const record = this.record();
    const serviceId = this.serviceId();
    if (!record || serviceId == null) {
      this.linkedinError.set('Contact details are still loading.');
      return;
    }
    const locationId = this.locationId();
    const companyName = record.company_name || '';
    const fallbackQuery = this.buildDefaultSearchQuery(companyName);
    const manualQuery = this.searchQuery().trim();
    if (manualQuery) {
      this.searchQuery.set(manualQuery);
    }
    const searchQuery = manualQuery || fallbackQuery || null;
    if (!manualQuery && !fallbackQuery) {
      this.searchQuery.set('');
    }
    this.linkedinError.set('');
    this.linkedinLoading.set(true);
    this.collectDataService
      .refreshLinkedInProfileDetails(
        serviceId,
        record.id,
        locationId ?? null,
        searchQuery
      )
      .subscribe({
        next: () => {
          this.loadStoredLinkedInProfiles(
            serviceId,
            locationId ?? null,
            record.id,
            { preserveLoadingState: true }
          );
        },
        error: (error) => {
          this.linkedinLoading.set(false);
          this.linkedinError.set(this.extractErrorMessage(error));
        }
      });
  }

  protected onSearchSubmit(event: Event): void {
    event.preventDefault();
    if (this.linkedinLoading()) {
      return;
    }
    this.refreshLinkedIn();
  }

  protected onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value ?? '';
    this.searchQuery.set(value);
  }

  protected onTableFilterInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.tableFilterQuery.set(target?.value ?? '');
  }

  protected resetSearchQuery(): void {
    const record = this.record();
    this.applyDefaultSearchQuery(record?.company_name);
  }

  protected renderList(value: string[] | null): string[] {
    return Array.isArray(value) ? value : [];
  }

  private loadContact(
    serviceId: number,
    locationId: number,
    contactId: number
  ): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.record.set(null);
    this.linkedinDetails.set(null);
    this.linkedinError.set('');
    this.searchQuery.set('');
    this.tableFilterQuery.set('');

    this.collectDataService
      .getCompanyContactDetails(serviceId, locationId)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (response: CompanyContactResponse) => {
          const matched =
            response.data.find((item) => item.id === contactId) ?? null;

          if (!matched) {
            this.errorMessage.set(
              'Contact details were not found. They may have been removed.'
            );
            return;
          }

          this.record.set(matched);
          this.applyDefaultSearchQuery(matched.company_name);
          this.loadStoredLinkedInProfiles(serviceId, locationId, matched.id);
    },
    error: (error) => {
      this.errorMessage.set(this.extractErrorMessage(error));
    }
  });
  }

  private applyDefaultSearchQuery(
    _companyName: string | null | undefined
  ): void {
    this.searchQuery.set('');
  }

  private loadStoredLinkedInProfiles(
    serviceId: number,
    locationId: number | null,
    contactId: number,
    options?: { preserveLoadingState?: boolean }
  ): void {
    if (!options?.preserveLoadingState) {
      this.linkedinLoading.set(true);
    }
    this.linkedinError.set('');
    this.linkedinDetails.set(null);

    this.collectDataService
      .getLinkedInProfileDetails(serviceId, contactId, locationId ?? null)
      .pipe(
        finalize(() => {
          this.linkedinLoading.set(false);
        })
      )
      .subscribe({
        next: (response) => {
          if ((response.status || '').toLowerCase() !== 'success') {
            this.linkedinError.set(
              response.message || 'Unable to load LinkedIn profile details.'
            );
            this.linkedinDetails.set(null);
            return;
          }
          this.linkedinDetails.set(response);
        },
        error: (error) => {
          this.linkedinDetails.set(null);
          this.linkedinError.set(this.extractErrorMessage(error));
        }
      });
  }

  private extractErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return 'An unexpected error occurred.';
    }
    const maybeResponse = error as {
      error?: unknown;
      message?: string;
      detail?: string;
      statusText?: string;
    };
    const nested = maybeResponse?.error;
    let nestedMessage: string | null = null;
    if (typeof nested === 'string') {
      nestedMessage = nested;
    } else if (nested && typeof nested === 'object') {
      const detail = (nested as { detail?: string }).detail;
      const message = (nested as { message?: string }).message;
      nestedMessage = detail || message || null;
    }
    return (
      nestedMessage ||
      maybeResponse?.detail ||
      maybeResponse?.message ||
      maybeResponse?.statusText ||
      'An unexpected error occurred.'
    );
  }

  private buildProfileRow(
    profile: LinkedInProfileSummary
  ): LinkedInProfileTableRow {
    const summary = profile.summary?.trim() || '';
    const rawName =
      profile.name?.trim() ||
      this.extractNameFromSummary(summary) ||
      this.extractNameFromSections(profile.sections) ||
      null;
    const positionRaw =
      profile.position?.trim() ||
      this.extractPosition(profile, summary) ||
      this.extractFirstHighlight(profile.sections) ||
      null;
    const experienceRaw =
      profile.experience?.trim() ||
      this.extractExperience(profile.sections) ||
      (summary ? this.getSecondSentence(summary) : '') ||
      null;
    const contactInfoRaw =
      profile.contact_info?.trim() ||
      this.extractContactInfo(profile.sections) ||
      this.extractContactPatterns(summary) ||
      null;
    const profileUrl = this.normalizeProfileUrl(profile.profile_url);

    return {
      name: this.normalizeLinkedInText(rawName, 'LinkedIn Member'),
      position: this.normalizeLinkedInText(positionRaw),
      experience: this.normalizeLinkedInText(experienceRaw),
      profileUrl,
      contactInfo: this.normalizeLinkedInText(contactInfoRaw)
    };
  }

  private normalizeLinkedInText(
    raw: string | null | undefined,
    fallback = 'N/A'
  ): string {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) {
      return fallback;
    }
    const lowered = trimmed.toLowerCase();
    const blockedPhrases = [
      'join linkedin',
      'sign in',
      'sign in to view',
      'linkedin member',
      'member',
      'log in or sign up',
      'join now'
    ];
    if (blockedPhrases.some((phrase) => lowered === phrase || lowered.startsWith(phrase))) {
      return fallback;
    }
    return trimmed;
  }

  private normalizeProfileUrl(raw: string | null | undefined): string {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) {
      return 'N/A';
    }
    return trimmed;
  }

  private extractNameFromSummary(summary: string): string | null {
    if (!summary) {
      return null;
    }
    const profileMatch = summary.match(
      /view\s+(.+?)['’]s\s+profile\s+on\s+linkedin/i
    );
    if (profileMatch?.[1]) {
      return this.formatName(profileMatch[1]);
    }
    const possessiveMatch = summary.match(/^\s*([^.'’]+?)['’]s\b/);
    if (possessiveMatch?.[1]) {
      return this.formatName(possessiveMatch[1]);
    }
    return null;
  }

  private extractNameFromSections(
    sections: LinkedInProfileSection[] | undefined
  ): string | null {
    if (!Array.isArray(sections)) {
      return null;
    }
    for (const section of sections) {
      const title = section?.title?.trim().toLowerCase();
      if (!title) {
        continue;
      }
      if (title.includes('about')) {
        const candidate = section.highlights?.[0];
        if (candidate) {
          const match = candidate.match(/^([A-Z][A-Za-z\s.-]{1,60})/);
          if (match?.[1]) {
            return this.formatName(match[1]);
          }
        }
      }
    }
    return null;
  }

  private extractPosition(
    profile: LinkedInProfileSummary,
    summary: string
  ): string | null {
    const experienceSection = this.findSection(profile.sections, (title) =>
      title.includes('experience')
    );
    if (experienceSection?.highlights?.length) {
      return experienceSection.highlights[0];
    }
    const sentences = this.splitSentences(summary);
    for (const sentence of sentences) {
      if (sentence.toLowerCase().includes(' is ')) {
        return sentence;
      }
    }
    return null;
  }

  private extractExperience(
    sections: LinkedInProfileSection[] | undefined
  ): string | null {
    if (!Array.isArray(sections)) {
      return null;
    }
    const experienceSection = this.findSection(sections, (title) =>
      title.includes('experience')
    );
    if (experienceSection?.highlights?.length) {
      return experienceSection.highlights.slice(0, 3).join('; ');
    }
    const summarySection = this.findSection(sections, (title) =>
      title.includes('summary')
    );
    if (summarySection?.highlights?.length) {
      return summarySection.highlights.slice(0, 3).join('; ');
    }
    return null;
  }

  private extractContactInfo(
    sections: LinkedInProfileSection[] | undefined
  ): string | null {
    if (!Array.isArray(sections)) {
      return null;
    }
    const contactSection = this.findSection(sections, (title) =>
      ['contact', 'reach', 'email', 'phone'].some((keyword) =>
        title.includes(keyword)
      )
    );
    if (contactSection?.highlights?.length) {
      return contactSection.highlights.join('; ');
    }
    for (const section of sections) {
      for (const highlight of section.highlights ?? []) {
        if (this.containsContactCue(highlight)) {
          return highlight;
        }
      }
    }
    return null;
  }

  private extractContactPatterns(summary: string): string | null {
    if (!summary) {
      return null;
    }
    const emailMatch = summary.match(
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/
    );
    if (emailMatch?.[0]) {
      return emailMatch[0];
    }
    const phoneMatch = summary.match(
      /\+?\d[\d\s().-]{7,20}\d/
    );
    if (phoneMatch?.[0]) {
      return phoneMatch[0].trim();
    }
    return null;
  }

  private extractFirstHighlight(
    sections: LinkedInProfileSection[] | undefined
  ): string | null {
    if (!Array.isArray(sections)) {
      return null;
    }
    for (const section of sections) {
      const highlight = section.highlights?.[0];
      if (highlight) {
        return highlight;
      }
    }
    return null;
  }

  private findSection(
    sections: LinkedInProfileSection[] | undefined,
    predicate: (title: string) => boolean
  ): LinkedInProfileSection | null {
    if (!Array.isArray(sections)) {
      return null;
    }
    for (const section of sections) {
      const title = section?.title?.trim().toLowerCase();
      if (title && predicate(title)) {
        return section;
      }
    }
    return null;
  }

  private splitSentences(summary: string): string[] {
    return summary
      .split(/[.!?]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private getSecondSentence(summary: string): string | null {
    const sentences = this.splitSentences(summary);
    if (sentences.length > 1) {
      return sentences[1];
    }
    return null;
  }

  private formatName(raw: string): string {
    return raw
      .split(/\s+/)
      .map((part) =>
        part.length
          ? part.charAt(0).toUpperCase() + part.slice(1)
          : part
      )
      .join(' ')
      .trim();
  }

  private containsContactCue(text: string | undefined): boolean {
    if (!text) {
      return false;
    }
    const lowered = text.toLowerCase();
    return (
      lowered.includes('email') ||
      lowered.includes('@') ||
      lowered.includes('phone') ||
      lowered.includes('call') ||
      lowered.includes('contact') ||
      /\+?\d[\d\s().-]{7,20}\d/.test(text)
    );
  }

  private buildDefaultSearchQuery(
    companyName: string | null | undefined
  ): string | null {
    const name = (companyName ?? '').trim();
    if (!name) {
      return null;
    }
    return `site:linkedin.com/in "${name}" current`;
  }
}
